import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {getStationIdFromLocalStorage} from '../../../services/parametre-document/parametre-document-state.service';
import {ParamTableRowPayload, ParamTablesService} from '../../../services/parametrage/param-tables.service';

export interface ParametreDbField {
  libelle: string;
  type: ParametreDbType;
  /** Ex. `param1` — renvoyé par GET param-tables/by-table/... pour conserver la clé à l’enregistrement. */
  champ_db?: string;
}

export type ParametreDbType = 'varchar' | 'date' | 'nombre' | 'select';

export type ParametreDbSection = 'produit' | 'client' | 'fournisseur' | 'stock';

@Component({
  selector: 'app-parametre-db',
  templateUrl: './parametre-db.component.html',
  styleUrls: ['./parametre-db.component.scss']
})
export class ParametreDbComponent implements OnInit, OnChanges, OnDestroy {

  /** Mode intégré dans l’écran « Paramètres des données » (onglet Champs). */
  @Input() embedInParent = false;
  @Input() embedSection: ParametreDbSection | null = null;
  /** Masque le titre de page lorsque le parent affiche déjà un en-tête. */
  @Input() hidePageHeader = false;

  readonly typeOptions: { value: ParametreDbType; label: string }[] = [
    { value: 'varchar', label: 'VARCHAR' },
    { value: 'date', label: 'Date' },
    { value: 'nombre', label: 'Nombre' },
    { value: 'select', label: 'Liste (select)' }
  ];

  readonly validSections: ParametreDbSection[] = ['produit', 'client', 'fournisseur', 'stock'];

  section: ParametreDbSection = 'produit';

  private readonly sectionLabels: Record<ParametreDbSection, string> = {
    produit: 'Produit',
    client: 'Client',
    fournisseur: 'Fournisseur',
    stock: 'Stock'
  };

  /**
   * Segment d’URL GET `/api/parametrage/param-tables/by-table/:table?...`
   * (et `table_db` côté POST si cohérent avec le backend).
   */
  private readonly sectionToTableDb: Record<ParametreDbSection, string> = {
    produit: 'products',
    client: 'client',
    fournisseur: 'Fournisseur',
    stock: 'stock'
  };

  saving = false;
  loading = false;

  private parametresBySection: Record<ParametreDbSection, ParametreDbField[]> = {
    produit: [{ libelle: '', type: 'varchar' }],
    client: [{ libelle: '', type: 'varchar' }],
    fournisseur: [{ libelle: '', type: 'varchar' }],
    stock: [{ libelle: '', type: 'varchar' }]
  };

  private destroy$ = new Subject<void>();

  constructor(
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private paramTablesService: ParamTablesService
  ) {}

  get parametres(): ParametreDbField[] {
    return this.parametresBySection[this.section];
  }

  get sectionTitle(): string {
    return this.sectionLabels[this.section];
  }

  ngOnInit(): void {
    if (this.embedInParent && this.embedSection != null) {
      this.section = this.embedSection;
      this.loadParametresDepuisApi();
      return;
    }

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const raw = params['section'] as string | undefined;
      if (!raw || !this.isValidSection(raw)) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { section: 'produit' },
          replaceUrl: true
        });
        return;
      }
      this.section = raw;
      this.loadParametresDepuisApi();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.embedInParent || this.embedSection == null) {
      return;
    }
    const ch = changes['embedSection'];
    if (ch && !ch.firstChange) {
      this.section = this.embedSection;
      this.loadParametresDepuisApi();
    }
  }

  /**
   * GET `/api/parametrage/param-tables/by-table/:table?station_id=&user_id=`
   * (`:table` = products | client | Fournisseur | stock selon la section).
   */
  private loadParametresDepuisApi(): void {
    const { stationId, userId } = this.getSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.error('Session invalide : station ou utilisateur introuvable.', 'Erreur');
      this.resetSectionCouranteALigneVide();
      return;
    }

    const tableDb = this.sectionToTableDb[this.section];
    this.loading = true;
    this.paramTablesService
      .getParamTablesByTable(tableDb, stationId, userId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (raw) => {
          const rows = this.normalizeParamTableApiResponse(raw);
          if (rows.length === 0) {
            this.resetSectionCouranteALigneVide();
          } else {
            this.parametresBySection[this.section] = rows;
          }
        },
        error: (err) => {
          const msg =
            err?.error?.message ||
            err?.message ||
            'Impossible de charger les paramètres.';
          this.toastr.error(msg, 'Erreur');
          this.resetSectionCouranteALigneVide();
        }
      });
  }

  private resetSectionCouranteALigneVide(): void {
    this.parametresBySection[this.section] = [{ libelle: '', type: 'varchar' }];
  }

  /** Même logique que `add-product.normalizeParamTableResponse` → libellé + type + champ_db. */
  private normalizeParamTableApiResponse(raw: unknown): ParametreDbField[] {
    const anyRaw = raw as Record<string, unknown> | unknown[] | null | undefined;
    let list: unknown[] = [];
    if (Array.isArray(anyRaw)) {
      list = anyRaw;
    } else if (anyRaw && typeof anyRaw === 'object') {
      const o = anyRaw as Record<string, unknown>;
      const cand =
        o['data'] ??
        o['rows'] ??
        o['param_tables'] ??
        o['parametrage'] ??
        o['items'];
      list = Array.isArray(cand) ? cand : [];
    }
    const rows: ParametreDbField[] = [];
    for (let i = 0; i < list.length; i++) {
      const row = list[i] as Record<string, unknown>;
      if (!row || typeof row !== 'object') {
        continue;
      }
      const lib = (row['libelle_champ'] ?? row['libelle'] ?? row['label'] ?? '') as string;
      const typeRaw = (row['type'] ??
        row['type_champ'] ??
        row['champ_type'] ??
        row['param_type'] ??
        row['data_type'] ??
        'varchar') as string;
      const champ =
        (row['champ_db'] ?? row['champDb'] ?? `param${i + 1}`) as string;
      rows.push({
        libelle: lib != null ? String(lib) : '',
        type: this.normalizeParamTypeFromApi(typeRaw),
        champ_db: champ != null ? String(champ) : `param${i + 1}`
      });
    }
    return rows;
  }

  private normalizeParamTypeFromApi(t: string): ParametreDbType {
    const s = (t || '').toLowerCase().trim();
    if (s === 'date' || s === 'datetime') {
      return 'date';
    }
    if (
      s === 'nombre' ||
      s === 'number' ||
      s === 'double' ||
      s === 'numeric' ||
      s === 'int' ||
      s === 'integer' ||
      s === 'float' ||
      s === 'decimal'
    ) {
      return 'nombre';
    }
    if (s === 'select' || s === 'liste' || s === 'dropdown' || s === 'list') {
      return 'select';
    }
    if (s === 'varchar' || s === 'text' || s === 'string' || s === 'char') {
      return 'varchar';
    }
    return 'varchar';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isValidSection(s: string): s is ParametreDbSection {
    return this.validSections.includes(s as ParametreDbSection);
  }

  ajouterParametre(): void {
    this.parametres.push({ libelle: '', type: 'varchar' });
  }

  supprimerLigne(index: number): void {
    if (this.parametres.length <= 1) {
      return;
    }
    this.parametres.splice(index, 1);
  }

  enregistrer(): void {
    const { stationId, userId } = this.getSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.error('Session invalide : station ou utilisateur introuvable.', 'Erreur');
      return;
    }

    const tableDb = this.sectionToTableDb[this.section];
    const rows: ParamTableRowPayload[] = [];
    this.parametres.forEach((p, i) => {
      const lib = (p.libelle || '').trim();
      if (!lib) {
        return;
      }
      const champDb =
        (p.champ_db && String(p.champ_db).trim()) || `param${i + 1}`;
      rows.push({
        station_id: stationId,
        user_id: userId,
        table_db: tableDb,
        champ_db: champDb,
        libelle_champ: lib,
        type: p.type
      });
    });

    if (rows.length === 0) {
      this.toastr.warning('Indiquez au moins un libellé avant d\'enregistrer.', 'Attention');
      return;
    }

    this.saving = true;
    this.paramTablesService
      .postParamTableRows(rows)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success(`Paramètres « ${this.sectionTitle} » enregistrés.`, 'Succès');
          this.loadParametresDepuisApi();
        },
        error: (err) => {
          const msg =
            err?.error?.message ||
            err?.message ||
            'Erreur lors de l\'enregistrement des paramètres.';
          this.toastr.error(msg, 'Erreur');
        }
      });
  }

  private getSessionIds(): { stationId: number; userId: number } {
    try {
      const raw = localStorage.getItem('currentUser');
      if (raw == null || raw === '') {
        return { stationId: 0, userId: 0 };
      }
      const currentUser = JSON.parse(raw) as Record<string, unknown>;
      const userRaw = currentUser['id'] ?? currentUser['user_id'];
      const userId = Number(userRaw);
      const stationId = this.getConnectedStationIdFromUser(currentUser);
      return {
        stationId: Number.isFinite(stationId) ? Math.floor(stationId) : 0,
        userId: Number.isFinite(userId) ? Math.floor(userId) : 0
      };
    } catch {
      return { stationId: 0, userId: 0 };
    }
  }

  /** Station connectée (`isConnecte === 1`) si présente, sinon même logique que `getStationIdFromLocalStorage`. */
  private getConnectedStationIdFromUser(currentUser: Record<string, unknown>): number {
    const stations = currentUser['stations'];
    if (Array.isArray(stations) && stations.length > 0) {
      const list = stations as Record<string, unknown>[];
      const connected = list.find((s) => s['isConnecte'] === 1);
      const st = connected ?? list[0];
      const sid = st?.['station_id'] ?? st?.['id'];
      const n = Number(sid);
      if (Number.isFinite(n) && n > 0) {
        return Math.floor(n);
      }
    }
    return getStationIdFromLocalStorage();
  }

}
